import { useEffect, useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Brain, Moon, Heart, Leaf, Users, Briefcase, Baby, Sparkles } from "lucide-react";
import axios from "axios";

import { API } from "@/apiConfig";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: "easeOut" },
});

const DEFAULT_PAGE = {
  page_kicker: "Accompagnements",
  page_title: "Comment puis-je vous accompagner ?",
  intro_text: "Chaque parcours est unique. Je propose des accompagnements personnalisés, adaptés à vos besoins, vos objectifs et votre rythme de vie.",
  header_image: "",
  header_image_alt: "Accompagnement sophrologique",
  cta_title: "Une question ? Un accompagnement ?",
  cta_text: "N'hésitez pas à me contacter pour discuter de vos besoins avant de prendre rendez-vous.",
  cta_primary_label: "Prendre rendez-vous",
  cta_secondary_label: "Voir les tarifs",
};

const DEFAULT_ITEMS = [
  { icon: Brain, title: "Stress & anxiété", color: "bg-sage/10 text-sage", desc: "Le stress chronique fragilise notre santé. Grâce à des exercices de sophrologie, vous apprendrez à réguler vos réponses au stress, à calmer le mental et à retrouver un équilibre durable.", tags: ["Burnout", "Anxiété généralisée", "Pression professionnelle"] },
  { icon: Moon, title: "Troubles du sommeil", color: "bg-terracotta/10 text-terracotta", desc: "Insomnies, réveils nocturnes, difficulté à s'endormir... La sophrologie propose des techniques spécifiques pour préparer le corps et l'esprit au repos et retrouver des nuits paisibles.", tags: ["Insomnie", "Réveils nocturnes", "Cauchemars"] },
  { icon: Heart, title: "Confiance & estime de soi", color: "bg-sage/10 text-sage", desc: "Renforcer l'image positive de soi, développer ses ressources intérieures et s'affirmer dans sa vie personnelle et professionnelle.", tags: ["Prise de parole", "Leadership", "Affirmation de soi"] },
  { icon: Sparkles, title: "Préparation mentale", color: "bg-terracotta/10 text-terracotta", desc: "Sportifs, artistes, étudiants : préparez vos performances avec des techniques de visualisation et de gestion du stress spécifiques à vos défis.", tags: ["Sport", "Examens", "Entretiens d'embauche"] },
  { icon: Leaf, title: "Gestion des émotions", color: "bg-sage/10 text-sage", desc: "Mieux comprendre et accueillir ses émotions, trouver une juste distance par rapport à ce qui nous affecte, développer une stabilité émotionnelle.", tags: ["Colère", "Tristesse", "Peur"] },
  { icon: Baby, title: "Enfants & adolescents", color: "bg-terracotta/10 text-terracotta", desc: "La sophrologie s'adapte parfaitement aux enfants et adolescents. Gestion de l'hyperactivité, des peurs, du stress scolaire, des troubles du sommeil...", tags: ["Stress scolaire", "Hyperactivité", "Peurs"] },
  { icon: Users, title: "Maternité & périnatalité", color: "bg-sage/10 text-sage", desc: "Accompagnement de la grossesse, préparation à l'accouchement et soutien dans la période post-natale pour une maternité vécue en pleine conscience.", tags: ["Grossesse", "Accouchement", "Post-natal"] },
  { icon: Briefcase, title: "Entreprise & collectif", color: "bg-terracotta/10 text-terracotta", desc: "Ateliers et formations en entreprise pour améliorer le bien-être au travail, réduire l'absentéisme et développer les compétences relationnelles.", tags: ["Équipes", "Management", "QVT"] },
];

export default function Accompagnements({ initialPage }) {
  const [content, setContent] = useState(DEFAULT_PAGE);

  useLayoutEffect(() => {
    if (initialPage?.content) setContent(prev => ({ ...prev, ...initialPage.content }));
  }, [initialPage]);

  useEffect(() => {
    axios.get(`${API}/public/pages/accompagnements`)
      .then(r => { if (r.data?.content) setContent(prev => ({ ...prev, ...r.data.content })); })
      .catch(() => {});
  }, []);

  const g = key => content[key] || DEFAULT_PAGE[key] || "";
  const accompagnements = DEFAULT_ITEMS.map((item, i) => {
    const n = i + 1;
    const tags = content[`item${n}_tags`]
      ? String(content[`item${n}_tags`]).split("\n").map(t => t.trim()).filter(Boolean)
      : item.tags;
    return {
      ...item,
      title: content[`item${n}_title`] || item.title,
      desc: content[`item${n}_desc`] || item.desc,
      tags,
      image: content[`item${n}_image`] || "",
      imageAlt: content[`item${n}_image_alt`] || content[`item${n}_title`] || item.title,
    };
  });

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="section-gap bg-bone">
        <div className="section-container">
          <div className="max-w-3xl">
            <motion.p {...fadeUp()} className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{g("page_kicker")}</motion.p>
            <motion.h1 {...fadeUp(0.1)} className="font-playfair text-5xl md:text-6xl text-charcoal mb-6">{g("page_title")}</motion.h1>
            <motion.p {...fadeUp(0.2)} className="font-manrope text-charcoal-muted text-lg leading-relaxed">
              {g("intro_text")}
            </motion.p>
          </div>
          {g("header_image") && (
            <motion.img {...fadeUp(0.25)} src={g("header_image")} alt={g("header_image_alt")}
              className="mt-10 rounded-[2rem] w-full max-h-[420px] object-cover shadow-lg" />
          )}
        </div>
      </section>

      {/* Cards bento grid */}
      <section className="section-gap bg-bone-light">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {accompagnements.map((a, i) => (
              <motion.div key={i} {...fadeUp(i * 0.07)} data-testid={`accompagnement-${i}`} className="card-soft group">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${a.color}`}>
                  <a.icon size={20} />
                </div>
                {a.image && <img src={a.image} alt={a.imageAlt} className="mb-4 h-44 w-full rounded-xl object-cover" />}
                <h3 className="font-playfair text-2xl text-charcoal mb-3">{a.title}</h3>
                <p className="font-manrope text-sm text-charcoal-muted leading-relaxed mb-4">{a.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {a.tags.map(t => (
                    <span key={t} className="font-manrope text-xs bg-sage/10 text-sage-dark px-3 py-1 rounded-full">{t}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-bone">
        <div className="section-container text-center">
          <motion.h2 {...fadeUp()} className="font-playfair text-4xl text-charcoal mb-4">{g("cta_title")}</motion.h2>
          <motion.p {...fadeUp(0.1)} className="font-manrope text-charcoal-muted mb-8 max-w-md mx-auto">
            {g("cta_text")}
          </motion.p>
          <motion.div {...fadeUp(0.2)} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prendre-rendez-vous" className="btn-primary inline-flex items-center gap-2">{g("cta_primary_label")} <ArrowRight size={16} /></Link>
            <Link to="/seances-tarifs" className="btn-secondary">{g("cta_secondary_label")}</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
